import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Inbox, Wine, Tags, Map, BarChart3, Settings2, LogOut, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import { adminApi, TOKEN_KEY } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Enquiries", icon: Inbox, end: true },
  { to: "/admin/brands", label: "Brands", icon: Wine },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/regions", label: "Regions", icon: Map },
  { to: "/admin/statistics", label: "Statistics", icon: BarChart3 },
  { to: "/admin/settings", label: "Company Settings", icon: Settings2 },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      navigate("/admin/login");
      return;
    }
    adminApi
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {});
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center">
        <p className="text-sm text-neutral-400" data-testid="admin-loading">
          Loading console…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex" data-testid="admin-layout">
      <aside className="w-60 shrink-0 bg-white border-r border-neutral-200 flex flex-col">
        <div className="px-5 py-5 border-b border-neutral-100">
          <Logo width={104} />
          <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-neutral-400">Admin Console</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  isActive ? "bg-[#4B0F1E] text-white" : "text-neutral-600 hover:bg-neutral-100"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-neutral-100 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-500 hover:bg-neutral-100 rounded-md"
          >
            <ExternalLink size={15} strokeWidth={1.75} />
            View website
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-neutral-500 hover:bg-neutral-100 rounded-md"
          >
            <LogOut size={15} strokeWidth={1.75} />
            Sign out
          </button>
          <p className="px-3 pt-2 text-[11px] text-neutral-400 truncate">{user.email}</p>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-8 py-8 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
